/**
 * My Points page model (TECH-POINTS-PAGE.md) — pure, no JSX.
 *
 * Turns the existing endpoints' payloads into the three-level disclosure the
 * page renders: period → day rows → job groups → task lines. Day totals come
 * straight from /api/me/point-totals-by-day (the engine's per-day source of
 * truth); events and adjustments only feed the expansions. Rejected events
 * are always kept in the model with their reason — a total dropping silently
 * is a trust-killer. Void events (system-superseded duplicates) are hidden.
 */
import { orgDateKey, orgTodayKey } from "@/lib/datetime";
import { computePayPeriod, type PayPeriod } from "@/lib/pay-period";

// ─── API row shapes (as the endpoints actually return them) ─────────────────

export interface ApiDayTotal {
    day: string; // YYYY-MM-DD
    provisionalPoints: number;
    approvedPoints: number;
}

export interface ApiPointEvent {
    id: string;
    job_id: string | null;
    job_title: string | null;
    job_display_number: string | null;
    task_key: string;
    display_label: string | null;
    quantity: number | string;
    provisional_points: number | string;
    payable_points: number | string | null;
    status: string; // provisional | approved | rejected | void
    rejection_reason: string | null;
    recorded_at: string; // ISO timestamp; fallback day when work_date is missing
    /** YYYY-MM-DD the work was done: the day payroll pays the line on. */
    work_date?: string | null;
    /** Paid at 1.5x because the job was booked or quoted after hours. */
    after_hours?: boolean;
}

export interface ApiAdjustment {
    id: string;
    delta_points: number | string;
    reason: string;
    status: string; // pending | approved | rejected
    pay_period_start: string | null;
    pay_period_end: string | null;
    created_at: string;
}

// ─── Page model shapes ───────────────────────────────────────────────────────

export interface PeriodOption extends PayPeriod {
    label: string; // "Jul 6 – Jul 19"
    isCurrent: boolean;
}

export type TaskStatus = "pending" | "approved" | "rejected";

export interface TaskLine {
    id: string;
    label: string;
    quantity: number;
    points: number;
    status: TaskStatus;
    rejectionReason: string | null;
}

export interface JobGroup {
    jobId: string | null;
    /** "#072649815 · Cintas — MB2 Dental" (or "Not tied to a job"). */
    label: string;
    /** Countable points on this job that day (rejected excluded). */
    points: number;
    /** Chip: approved when every countable line is, pending otherwise. */
    status: TaskStatus;
    tasks: TaskLine[];
}

export interface AdjustmentLine {
    id: string;
    deltaPoints: number;
    reason: string;
    status: "pending" | "approved" | "rejected";
}

export interface DayRow {
    day: string; // YYYY-MM-DD
    label: string; // "Tue Jul 7"
    isToday: boolean;
    /** approved + pending, from point-totals-by-day. */
    totalPoints: number;
    approvedPoints: number;
    pendingPoints: number;
    isZero: boolean;
    jobs: JobGroup[];
    adjustments: AdjustmentLine[];
}

export interface PeriodSummary {
    totalPoints: number;
    approvedPoints: number;
    pendingPoints: number;
    /** Part of approvedPoints: earlier work paid in this period. */
    latePoints: number;
    /** Net delta of pending+approved adjustments for the period (0 = none). */
    adjustmentPoints: number;
}

export interface DayBar {
    day: string;
    points: number;
    isToday: boolean;
    isFuture: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const DAY_MS = 86_400_000;

/**
 * Date key of a UTC-midnight anchor produced by the period walk below. Pure
 * calendar arithmetic (the anchor IS that day at 00:00Z), so the UTC date is
 * the right one here — this is never used to ask what day it is now.
 */
function anchorDateKey(ref: Date): string {
    return ref.toISOString().slice(0, 10);
}

/**
 * The org-calendar day a timestamp falls on. #1692 — events and adjustments
 * bucket by the Central day (the server's /point-totals-by-day does too), so a
 * test closed at 8 PM lands on today's row, not tomorrow's.
 */
function orgDayOf(iso: string): string {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso.slice(0, 10) : orgDateKey(d);
}

function shortDate(day: string): string {
    return new Date(`${day}T00:00:00Z`).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
    });
}

export function dayLabel(day: string): string {
    return new Date(`${day}T00:00:00Z`).toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        timeZone: "UTC",
    });
}

export function periodLabel(period: PayPeriod): string {
    return `${shortDate(period.start)} – ${shortDate(period.end)}`;
}

function toStatus(raw: string): TaskStatus | null {
    if (raw === "provisional") return "pending";
    if (raw === "approved") return "approved";
    if (raw === "rejected") return "rejected";
    return null; // void → hidden
}

// ─── Period switcher ─────────────────────────────────────────────────────────

/** Current period first, then the previous `pastCount` periods (spec: 6). */
export function buildPeriodOptions(periodDays: number, today: Date = new Date(), pastCount = 6): PeriodOption[] {
    const options: PeriodOption[] = [];
    // #1692 — anchor on the org-calendar day: computePayPeriod reads the UTC
    // day, which on the last evening of a period is already the next one.
    let ref = new Date(Date.parse(`${orgDateKey(today)}T00:00:00Z`));
    for (let i = 0; i <= pastCount; i++) {
        const period = computePayPeriod(periodDays, ref);
        options.push({ ...period, label: periodLabel(period), isCurrent: i === 0 });
        ref = new Date(Date.parse(`${period.start}T00:00:00Z`) - DAY_MS);
    }
    return options;
}

// ─── Day / job / task model ──────────────────────────────────────────────────

export interface BuildDayModelInput {
    period: PayPeriod;
    totalsByDay: ApiDayTotal[];
    events: ApiPointEvent[];
    adjustments: ApiAdjustment[];
    /** YYYY-MM-DD on the org calendar (America/Chicago); injected for tests. */
    today?: string;
}

export function buildDayModel(input: BuildDayModelInput): DayRow[] {
    const today = input.today ?? orgTodayKey();
    const totalsMap = new Map(input.totalsByDay.map((t) => [t.day, t]));

    // Group events by org-calendar day, then job. Void lines are dropped entirely.
    const eventsByDay = new Map<string, Map<string | null, ApiPointEvent[]>>();
    for (const e of input.events) {
        const status = toStatus(e.status);
        if (!status) continue;
        // Trust audit Phase 1: the line sits on the day the WORK was done,
        // the same day payroll pays it on, not the day it was entered.
        const day = e.work_date ?? orgDayOf(e.recorded_at);
        let jobs = eventsByDay.get(day);
        if (!jobs) {
            jobs = new Map();
            eventsByDay.set(day, jobs);
        }
        const list = jobs.get(e.job_id) ?? [];
        list.push(e);
        jobs.set(e.job_id, list);
    }

    // Adjustments land on their created day, clamped into the period so a
    // correction entered after the period closes still shows inside it.
    const adjustmentsByDay = new Map<string, AdjustmentLine[]>();
    for (const a of input.adjustments) {
        const createdDay = orgDayOf(a.created_at);
        const day =
            createdDay < input.period.start
                ? input.period.start
                : createdDay > input.period.end
                  ? input.period.end
                  : createdDay;
        const list = adjustmentsByDay.get(day) ?? [];
        list.push({
            id: a.id,
            deltaPoints: Number(a.delta_points),
            reason: a.reason,
            status: a.status === "approved" ? "approved" : a.status === "rejected" ? "rejected" : "pending",
        });
        adjustmentsByDay.set(day, list);
    }

    // One row per elapsed day of the period, newest first. Future days of the
    // current period don't render (they haven't happened yet).
    const startMs = Date.parse(`${input.period.start}T00:00:00Z`);
    const endMs = Math.min(Date.parse(`${input.period.end}T00:00:00Z`), Date.parse(`${today}T00:00:00Z`));

    const rows: DayRow[] = [];
    for (let ms = endMs; ms >= startMs; ms -= DAY_MS) {
        const day = anchorDateKey(new Date(ms));
        const totals = totalsMap.get(day);
        const approvedPoints = totals?.approvedPoints ?? 0;
        const pendingPoints = totals?.provisionalPoints ?? 0;

        const jobGroups: JobGroup[] = [];
        const dayJobs = eventsByDay.get(day);
        if (dayJobs) {
            for (const [jobId, events] of dayJobs) {
                const tasks: TaskLine[] = events.map((e) => {
                    const status = toStatus(e.status)!;
                    const points =
                        status === "approved" && e.payable_points != null
                            ? Number(e.payable_points)
                            : Number(e.provisional_points);
                    return {
                        id: e.id,
                        label: `${e.display_label || e.task_key}${e.after_hours ? " · after hours 1.5×" : ""}`,
                        quantity: Number(e.quantity),
                        points,
                        status,
                        rejectionReason: e.rejection_reason,
                    };
                });
                const countable = tasks.filter((t) => t.status !== "rejected");
                const first = events[0];
                const label = jobId
                    ? `#${first.job_display_number ?? jobId.slice(0, 8)}${first.job_title ? ` · ${first.job_title}` : ""}`
                    : "Not tied to a job";
                jobGroups.push({
                    jobId,
                    label,
                    points: countable.reduce((sum, t) => sum + t.points, 0),
                    status:
                        countable.length === 0
                            ? "rejected"
                            : countable.every((t) => t.status === "approved")
                              ? "approved"
                              : "pending",
                    tasks,
                });
            }
        }
        jobGroups.sort((a, b) => b.points - a.points);

        const adjustments = adjustmentsByDay.get(day) ?? [];
        const totalPoints = approvedPoints + pendingPoints;
        rows.push({
            day,
            label: dayLabel(day),
            isToday: day === today,
            totalPoints,
            approvedPoints,
            pendingPoints,
            isZero: totalPoints === 0 && jobGroups.length === 0 && adjustments.length === 0,
            jobs: jobGroups,
            adjustments,
        });
    }
    return rows;
}

// ─── Hero / home-card summary (shared so the two totals can't diverge) ───────

/**
 * `latePoints`: approved work from an earlier period that pays in this one
 * (payroll carry rule, /me/point-summary latePoints). It is added to the
 * approved total so the page, the home card and the paycheck agree.
 */
export function summarize(days: DayRow[], latePoints = 0): PeriodSummary {
    let approved = 0;
    let pending = 0;
    let adjustment = 0;
    for (const d of days) {
        approved += d.approvedPoints;
        pending += d.pendingPoints;
        for (const a of d.adjustments) {
            if (a.status !== "rejected") adjustment += a.deltaPoints;
        }
    }
    return {
        totalPoints: approved + latePoints + pending,
        approvedPoints: approved + latePoints,
        pendingPoints: pending,
        latePoints,
        adjustmentPoints: adjustment,
    };
}

// ─── Mini bar chart (one bar per period day, future days empty) ──────────────

export function buildBars(period: PayPeriod, totalsByDay: ApiDayTotal[], today?: string): DayBar[] {
    const todayStr = today ?? orgTodayKey();
    const totalsMap = new Map(totalsByDay.map((t) => [t.day, t]));
    const startMs = Date.parse(`${period.start}T00:00:00Z`);
    const endMs = Date.parse(`${period.end}T00:00:00Z`);
    const bars: DayBar[] = [];
    for (let ms = startMs; ms <= endMs; ms += DAY_MS) {
        const day = anchorDateKey(new Date(ms));
        const totals = totalsMap.get(day);
        bars.push({
            day,
            points: (totals?.approvedPoints ?? 0) + (totals?.provisionalPoints ?? 0),
            isToday: day === todayStr,
            isFuture: day > todayStr,
        });
    }
    return bars;
}

/** Adjustments belong to the period whose payroll window they were booked to.
 *  pg DATE columns arrive as full ISO timestamps in JSON, so compare on the
 *  date part only. */
export function adjustmentsForPeriod(adjustments: ApiAdjustment[], period: PayPeriod): ApiAdjustment[] {
    return adjustments.filter(
        (a) =>
            (a.pay_period_start ?? "").slice(0, 10) === period.start &&
            (a.pay_period_end ?? "").slice(0, 10) === period.end,
    );
}
